<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'stationList' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <StationForm
      v-if="station || stationNew"
      :station="station"
      :is-loading="isLoading"
      :is-fetching="isFetching"
      @on-submit="onSubmit"
      @on-delete="onDelete"
      @refetch="refetch"
    />
    <div v-else class="flex justify-center"><Spinner /></div>
  </section>
</template>

<script lang="ts" setup>
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import { computed, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import StationForm from './StationForm.vue';
import {
  useCreateStation,
  useDeleteStation,
  useStation,
  useUpdateStation,
} from '@/queries/use-stations';
import type { CreateStationDto, UpdateStationDto } from '@/entities/station/station.entity';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { ref } from 'vue';
import { Alert } from '@/services/alert.service';

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();
const stationId = ref(route.params.id as string);
const stationNew = computed(() => stationId.value === 'new');
const stationFetchingEnabled = computed(() => !stationNew.value);

const { data: station, refetch, isFetching } = useStation(stationId, stationFetchingEnabled);
const { mutateAsync: createStation, isPending: isSaving } = useCreateStation();
const { mutateAsync: updateStation, isPending: isUpdating } = useUpdateStation();
const { mutateAsync: deleteStation, isPending: isDeleting } = useDeleteStation();

const isLoading = computed(() => isSaving.value || isUpdating.value || isDeleting.value);

watchEffect(() => {
  if (station.value) {
    uiStore.setHeaderTitle(
      t('station'),
      getLocalizedField(station.value.info),
      `${station.value.street}, ${station.value.zip} ${station.value.city}`,
    );
  } else {
    uiStore.setHeaderTitle(t('station'));
  }
});

const onSubmit = async (formValues: UpdateStationDto) => {
  if (!stationNew.value) {
    updateStation({
      id: stationId.value,
      station: formValues,
    });
  } else {
    const { id } = await createStation(formValues as CreateStationDto);
    router.replace({ name: 'station', params: { id: id } });
    stationId.value = id;
  }
};

const onDelete = async () => {
  if (!station.value?.id) return;
  const { isConfirmed } = await Alert.fire({
    titleText: t('reallyDeleteStationTitle'),
    text: t('reallyDeleteStationText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('deleteStation'),
    denyButtonText: t('dontDelete'),
  });
  if (!isConfirmed) return;
  await deleteStation(station.value.id);
  await router.push({ name: 'stationList' });
};
</script>

<i18n lang="json">
{
  "en": {
    "station": "Station",
    "deleteStation": "Delete station",
    "reallyDeleteStationTitle": "Really delete station?",
    "reallyDeleteStationText": "The station will be removed (soft deleted).",
    "dontDelete": "Don't delete"
  },
  "de": {
    "station": "Station",
    "deleteStation": "Station löschen",
    "reallyDeleteStationTitle": "Station wirklich löschen?",
    "reallyDeleteStationText": "Die Station wird entfernt (soft deleted).",
    "dontDelete": "Nicht löschen"
  },
  "es": {
    "station": "Estación",
    "deleteStation": "Eliminar estación",
    "reallyDeleteStationTitle": "¿Eliminar la estación?",
    "reallyDeleteStationText": "La estación se eliminará (borrado lógico).",
    "dontDelete": "No eliminar"
  }
}
</i18n>
